/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { ICheckoutData, ICheckoutViewProps, ILineItem, ILineItemDeliveryGroup, ILineItems, IOrderSummary, IPickUpAtStore } from '@msdyn365-commerce-modules/checkout';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { ICoreContext } from '@msdyn365-commerce/core-internal';
import { CartLine } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
import { brandCodeParser } from '../../../Utilities/brand-code-parser';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemComponent: React.FC<{ line: ILineItem; cartItem?: CartLine; context: ICoreContext }> = ({ line: { LineItem, item, pickUpAtStore }, cartItem, context }) => {
    let brandCode: string = '';
    if (cartItem) {
        brandCode = brandCodeParser({ description: cartItem.Description, appContext: context });
    }
    return (
        <Node {...LineItem}>
            {brandCode && <div className='msc-cart-line__brand'>{brandCode}</div>}
            {item}
            {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        </Node>
    );
};

export const LineItemGroupComponent: React.FC<{ group: ILineItemDeliveryGroup; data: ICheckoutData; context: ICoreContext }> = ({ group: { LineItemDeliveryGroup, LineItemList, heading, lineItems }, data, context }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => {
                // get the item and send it to the component
                const item = data.checkout.result?.checkoutCart.cart.CartLines?.find(cartItem => cartItem.LineId === lineItem.LineId);
                return (
                    <LineItemComponent key={lineItem.LineId} line={lineItem} cartItem={item} context={context} />
                );
            })}
        </Node>
    </Node>
);

export const LineItemsComponent: React.FC<{ lineItems: ILineItems; data: ICheckoutData; context: ICoreContext }> = ({ lineItems: { LineItems, Header, heading, editLink, itemsForPickup, itemsForShip }, data, context }) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsForPickup && <LineItemGroupComponent group={itemsForPickup} data={data} context={context} />}
        {itemsForShip && <LineItemGroupComponent group={itemsForShip} data={data} context={context} />}
    </Node>
);

const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines }) => (
    <div className='msc-order-summary-wrapper'>
        {heading}
        <div className='msc-order-summary__items'>
            {lines && (
                <>
                    {lines.subtotal}
                    {lines.shipping}
                    {lines.tax}
                    {lines.totalDiscounts}
                    {lines.loyalty}
                    {lines.giftCard}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

const CheckoutView: React.FC<ICheckoutViewProps> = props => {
    const {
        canShow,
        checkoutProps,
        mainProps,
        mainControlProps,
        sideProps,
        loading,
        alert,
        guidedForm,
        orderSummary,
        lineItems,
        placeOrderButton,
        data,
        context
    } = props;
    return (
        <Module {...checkoutProps}>
            {loading}
            {alert}
            {canShow && (
                <>
                    <Node {...mainProps}>
                        {guidedForm}
                        <Node {...mainControlProps}>
                            {placeOrderButton}
                        </Node>
                    </Node>
                    <Node {...sideProps}>
                        {orderSummary && <OrderSummaryComponent {...orderSummary} />}
                        {lineItems && <LineItemsComponent lineItems={lineItems} data={data} context={context} />}
                    </Node>
                </>
            )}
        </Module>
    );
};

export default CheckoutView;
